import { useMemo } from 'react';

import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import ClockIcon from '@atlaskit/icon/core/clock';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import HashtagIcon from '@atlaskit/icon/core/hashtag';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import NotificationsIcon from '@atlaskit/icon/core/notification';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import TelescopeIcon from '@atlaskit/icon-lab/core/telescope';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem } from '@townsquare/analytics-nav4';
import { useUnitsEnabled } from '@townsquare/collab-context';
import { useDashboardExperienceStatus } from '@townsquare/dashboards-view/hooks/useDashboardExperienceStatus';
import { useDiscoverPath } from '@townsquare/discover-view/hooks/use-discover-path';
import { filterUndefined } from '@townsquare/filter-type';
import { SideNavItem } from '@townsquare/griffin-navigation/types';
import { SideNavProductItem } from '@townsquare/navigation-config/nav4';
import { useNoOrgExperience } from '@townsquare/org-context';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import { dashboardRoute } from '@townsquare/routes/dashboard';
import { dashboardByIdRoute } from '@townsquare/routes/dashboard-by-id';
import { dashboardDirectoryRoute } from '@townsquare/routes/dashboard-directory';
import { discoverRouteDefinition } from '@townsquare/routes/discover';
import { goalUpdateViewRoute } from '@townsquare/routes/goal-updates-view';
import { notificationsRoute } from '@townsquare/routes/notifications';
import { projectUpdateViewRoute } from '@townsquare/routes/project-updates-view';
import { recentsViewRoute } from '@townsquare/routes/recents-view';
import { savedViewsUpdatesRoute } from '@townsquare/routes/saved-views-updates-view';
import { starredRouteDefinition } from '@townsquare/routes/starred';
import { tagFeedViewRoute } from '@townsquare/routes/tag-feed-view';
import { tagsDirectoryRoute } from '@townsquare/routes/tags-directory';
import { teamUpdatesViewRoute } from '@townsquare/routes/team-updates-view';
import { topicRoute } from '@townsquare/routes/topic-page';
import { usageRouteDefinition } from '@townsquare/routes/usage';
import { yourWorkRouteDefinition } from '@townsquare/routes/your-work';
import { fg } from '@townsquare/stat-sig/gate';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { DashboardsSideNavItem } from './DashboardsSideNavItem';
import { ExternalRedirectNavItem } from './ExternalRedirectNavItem';
import { useIsNotificationsSidebarEntryRemoved } from './use-is-notifications-sidebar-entry-removed';
import { useStatusUpdatesPath } from './use-status-updates-path';
import { useTagsDirectoryRoute } from './use-tags-directory-route';

type SideNavItemsCore = Record<
	| 'recent'
	| 'starred'
	| 'discover'
	| 'notifications'
	| 'updates'
	| 'tags'
	| 'dashboards'
	| 'yourWork'
	| 'usage',
	SideNavItem
>;

export const useSideNavItemsCore = (): SideNavItemsCore => {
	const intl = useIntl();
	const routeDependencies = useRouteDependencies();

	const unitsEnabled = useUnitsEnabled();

	const recentPath = generatePath(recentsViewRoute, routeDependencies);
	const starredPath = generatePath(starredRouteDefinition, routeDependencies);
	const notificationsPath = generatePath(notificationsRoute, routeDependencies);
	const dashboardsPath = generatePath(dashboardDirectoryRoute, routeDependencies);
	const usagePath = generatePath(usageRouteDefinition, routeDependencies);
	const yourWorkPath = generatePath(yourWorkRouteDefinition, {
		baseParams: { tabName: 'viewed' },
		...routeDependencies,
	});

	const { discoverPath } = useDiscoverPath();
	const { statusUpdatesPath } = useStatusUpdatesPath();
	const { tagsDirectoryPath } = useTagsDirectoryRoute();

	return useMemo(() => {
		return {
			recent: {
				id: SideNavProductItem.RECENT,
				analyticsSubject: AnalyticsSideNavItem.RECENT,
				path: recentPath,
				matches: [recentsViewRoute],
				name: intl.formatMessage({
					id: 'townsquare.atlassian-home.side-nav.recents',
					description: 'Side nav button to open recents page',
					defaultMessage: 'Recent',
				}),
				testId: 'side-nav-recents',
				Icon: ClockIcon,
			},
			starred: {
				id: SideNavProductItem.STARRED,
				analyticsSubject: AnalyticsSideNavItem.STARRED,
				path: starredPath,
				matches: [starredRouteDefinition],
				name: intl.formatMessage({
					id: 'townsquare.atlassian-home.side-nav.starred',
					description: 'Side nav button to open starred page',
					defaultMessage: 'Starred',
				}),
				testId: 'side-nav-starred',
				Icon: StarUnstarredIcon,
			},
			discover: {
				id: SideNavProductItem.DISCOVER,
				analyticsSubject: AnalyticsSideNavItem.DISCOVER,
				path: discoverPath,
				matches: [discoverRouteDefinition],
				name: intl.formatMessage({
					id: 'townsquare.home.use-side-nav-items.discover',
					description: 'Side nav button to open discover page',
					defaultMessage: 'Discover',
				}),
				testId: 'side-nav-discover',
				Icon: TelescopeIcon,
				Component: unitsEnabled ? ExternalRedirectNavItem : undefined,
			},
			notifications: {
				id: SideNavProductItem.NOTIFICATIONS,
				analyticsSubject: AnalyticsSideNavItem.NOTIFICATIONS,
				path: notificationsPath,
				matches: [notificationsRoute],
				name: intl.formatMessage({
					id: 'townsquare.atlassian-home.side-nav.notifications',
					description: 'Side nav button to open full page notifications',
					defaultMessage: 'Notifications',
				}),
				testId: 'side-nav-notifications',
				Icon: NotificationsIcon,
			},
			updates: {
				id: SideNavProductItem.UPDATES,
				analyticsSubject: AnalyticsSideNavItem.UPDATES,
				path: statusUpdatesPath,
				matches: [
					projectUpdateViewRoute,
					goalUpdateViewRoute,
					tagFeedViewRoute,
					savedViewsUpdatesRoute,
					teamUpdatesViewRoute,
				],
				name: intl.formatMessage({
					id: 'townsquare.home.use-side-nav-items.status-updates',
					description: 'Side nav button to open projects updates page',
					defaultMessage: 'Status updates',
				}),
				testId: 'side-nav-status-updates',
				Icon: MegaphoneIcon,
				Component: unitsEnabled ? ExternalRedirectNavItem : undefined,
			},
			tags: {
				id: SideNavProductItem.TAGS,
				analyticsSubject: AnalyticsSideNavItem.TAGS,
				path: tagsDirectoryPath,
				matches: [tagsDirectoryRoute, topicRoute],
				name: intl.formatMessage({
					id: 'townsquare.home.use-side-nav-items.tags',
					description: 'Side nav button to open topics tags',
					defaultMessage: 'Tags',
				}),
				testId: 'side-nav-tags',
				Icon: HashtagIcon,
				Component: unitsEnabled ? ExternalRedirectNavItem : undefined,
			},
			dashboards: {
				id: SideNavProductItem.DASHBOARDS,
				analyticsSubject: AnalyticsSideNavItem.DASHBOARDS,
				path: dashboardsPath,
				matches: [dashboardDirectoryRoute, dashboardRoute, dashboardByIdRoute],
				name: intl.formatMessage({
					id: 'townsquare.home.use-side-nav-items.dashboards',
					description: 'Side nav button to open dashboards tags',
					defaultMessage: 'Dashboards',
				}),
				testId: 'side-nav-dashboards',
				Icon: DashboardIcon,
				Component: DashboardsSideNavItem,
			},
			yourWork: {
				id: SideNavProductItem.YOUR_WORK,
				analyticsSubject: AnalyticsSideNavItem.YOUR_WORK,
				path: yourWorkPath,
				matches: [yourWorkRouteDefinition],
				name: intl.formatMessage({
					id: 'townsquare.home.use-side-nav-items.your-work',
					description: 'Side nav button to open your work page',
					defaultMessage: 'Your work',
				}),
				testId: 'side-nav-your-work',
				Icon: ListBulletedIcon,
			},
			usage: {
				id: SideNavProductItem.USAGE,
				analyticsSubject: AnalyticsSideNavItem.USAGE,
				path: usagePath,
				matches: [usageRouteDefinition],
				name: intl.formatMessage({
					id: 'townsquare.home.use-side-nav-items.usage',
					description: 'Side nav button to open the usage page',
					defaultMessage: 'Usage',
				}),
				testId: 'side-nav-usage',
				Icon: ChartBarIcon,
			},
		};
	}, [
		dashboardsPath,
		discoverPath,
		intl,
		notificationsPath,
		recentPath,
		starredPath,
		statusUpdatesPath,
		tagsDirectoryPath,
		unitsEnabled,
		usagePath,
		yourWorkPath,
	]);
};

export const useSideNavItems = (): SideNavItem[] => {
	const hasNoWorkspace = useNoWorkspaceExperience();
	const { isOptedIn: isDashboardOptedIn } = useDashboardExperienceStatus();
	const isDashboardsEnabled = fg('atlas_enable_dashboard_route') && isDashboardOptedIn;
	const noOrgExperience = useNoOrgExperience();
	const isYourWorkEnabled = !noOrgExperience && fg('townsquare_your_work_page');
	// Your Work replaces Starred if enabled
	const isStarredEnabled = !noOrgExperience && !isYourWorkEnabled;

	const { isDiscoverEnabled } = useDiscoverPath();
	const { isStatusUpdatesEnabled } = useStatusUpdatesPath();
	const { isTagsDirectoryEnabled } = useTagsDirectoryRoute();
	const isNotificationsSidebarEntryRemoved = useIsNotificationsSidebarEntryRemoved();
	const isUsageEnabled = fg('townsquare_home_usage_page');

	const { recent, starred, discover, notifications, updates, tags, dashboards, yourWork, usage } =
		useSideNavItemsCore();

	return [
		// Your Work replaces Recent if enabled
		!noOrgExperience && !isYourWorkEnabled ? recent : undefined,
		isStarredEnabled ? starred : undefined,
		isYourWorkEnabled ? yourWork : undefined,
		isDiscoverEnabled ? discover : undefined,
		isNotificationsSidebarEntryRemoved ? undefined : notifications,
		isStatusUpdatesEnabled ? updates : undefined,
		isTagsDirectoryEnabled ? tags : undefined,
		!hasNoWorkspace && isDashboardsEnabled ? dashboards : undefined,
		isUsageEnabled ? usage : undefined,
	].filter(filterUndefined);
};
